/* =====================================================================================
   Presenting a TOTP enrolment: the QR and the setup key.

   Separated from mfa.ts because none of it touches a session, a token or the database, and
   that makes it testable on its own. scripts/verify-mfa-qr.mjs generates a code, decodes it
   with an independent reader and compares the result against the originating otpauth URI,
   which is the only way to check "does this scan" without a phone in the loop.
   ===================================================================================== */


/*
 * Recover the SVG markup from what auth-js hands back.
 *
 * The server returns bare SVG. GoTrueClient then does, verbatim:
 *
 *     data.totp.qr_code = `data:image/svg+xml;utf-8,${data.totp.qr_code}`
 *
 * with no percent-encoding. That string is not a usable data URI: a QR SVG contains
 * fill="#000000", and a raw '#' inside a data URI is the fragment delimiter, so the browser
 * discards everything from the first '#' onwards and renders a truncated code. Which is
 * precisely a QR that displays but will not scan.
 *
 * Re-encoding would work, but stripping the prefix and inlining the markup is better: no
 * encoding step to get wrong, and the QR scales crisply to whatever size the CSS asks for.
 */
export function extractSvg(raw: string | null): string | null {
  if (!raw) return null;
  const s = raw.trim();
  const start = s.indexOf('<svg');
  if (start === -1) return null;        // base64 or PNG: handled by the <img> fallback.
  const end = s.lastIndexOf('</svg>');
  if (end === -1) return null;
  return addViewBox(s.slice(start, end + 6));
}


/*
 * Give the markup a viewBox if it has none.
 *
 * This matters more than it looks. Supabase's QR arrives as
 * `<svg width="231" height="231">` with its modules drawn in absolute user units and NO
 * viewBox. Without one, setting width/height in CSS resizes the VIEWPORT and does not scale
 * the CONTENT, so anything past the new edge is simply clipped away. Sizing that QR to 190px
 * silently removed the right and bottom edges, taking two of the three finder patterns with
 * them: a QR that looks entirely normal and cannot be decoded by anything.
 *
 * Verified by decoding both renderings: at its natural 231px it reads back the enrolment URI
 * exactly; clipped to 190px it does not read at all.
 */
export function addViewBox(svg: string): string {
  if (/viewBox\s*=/i.test(svg)) return svg;
  const w = svg.match(/<svg[^>]*\bwidth="(\d+(?:\.\d+)?)"/i);
  const h = svg.match(/<svg[^>]*\bheight="(\d+(?:\.\d+)?)"/i);
  if (!w || !h) return svg;
  return svg.replace(/<svg/i, `<svg viewBox="0 0 ${w[1]} ${h[1]}" preserveAspectRatio="xMidYMid meet"`);
}


/*
 * Draw the QR here rather than displaying the one Supabase sends.
 *
 * Not merely a workaround for the missing viewBox. Their SVG is 362,632 bytes because every
 * module, white ones included, is its own <rect>; this produces the same code in about 4,500
 * with a correct viewBox, so it scales to any size without clipping. It is generated from the
 * same otpauth URI the QR would have encoded, and the round trip is checked in the tests:
 * generate, decode, compare against the URI.
 */
export async function renderQr(uri: string): Promise<string | null> {
  try {
    // qrcode is CommonJS. Vite interops it so the named export is reachable directly, Node
    // hangs it off `.default`. Accept either, or this works in the browser and fails in the
    // test, which is the wrong way round.
    const mod = await import('qrcode');
    const QR = ((mod as unknown as { default?: typeof mod }).default ?? mod);
    const svg = await QR.toString(uri, {
      type: 'svg',
      margin: 2,                  // the quiet zone scanners need to find the symbol
      errorCorrectionLevel: 'M',
      color: { dark: '#000000ff', light: '#ffffffff' },
    });
    return sizeToWholeModules(svg);
  } catch (e) {
    // Falling back is correct, but doing it silently is how a broken QR reaches someone's
    // phone unnoticed. Say so, then fall back.
    console.warn('[mfa] could not generate the QR locally, falling back to the server image', e);
    return null;
  }
}


/** The original value, for the rare case where it is not SVG at all. */
export function qrImageSrc(raw: string | null): string | null {
  if (!raw) return null;
  return extractSvg(raw) ? null : raw;
}


/*
 * Base32 alphabet is A-Z and 2-7. Everything else is stripped rather than trusted: the '='
 * padding some encoders emit, any lower case, and any stray whitespace picked up on the way
 * through the clipboard are each enough for an authenticator to refuse the key outright.
 */
export function normaliseSecret(secret: string | null): string | null {
  if (!secret) return null;
  const cleaned = secret.toUpperCase().replace(/[^A-Z2-7]/g, '');
  return cleaned || null;
}

/*
 * Pin the QR to a whole number of pixels per module.
 *
 * The generated SVG is sized only by its viewBox, so CSS is free to scale it to any width.
 * At a fractional ratio some modules land on 3 pixels and their neighbours on 4, and the
 * irregular grid is enough to defeat a decoder: measured here, the same code reads at 140px
 * and 171px but not at 160px, purely because 160/57 is 2.81.
 *
 * Choosing the width from the module count instead removes the failure mode rather than
 * avoiding it, and the symbol count varies with the length of the account name, so it cannot
 * simply be hardcoded. The viewBox stays, so a narrow screen can still scale it down without
 * clipping.
 */
export function sizeToWholeModules(svg: string, target = 240): string {
  const box = svg.match(/viewBox="0 0 ([\d.]+)/);
  if (!box) return svg;
  const modules = Math.round(+box[1]);
  if (!modules) return svg;
  const scale = Math.max(4, Math.round(target / modules));
  const px = modules * scale;
  return svg.replace(/<svg/i, `<svg width="${px}" height="${px}"`);
}
